<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../../components/Modal.vue';
import FunnelForm from './FunnelForm.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import { useConfig } from 'dashboard/composables/useConfig';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  editingFunnel: {
    type: Object,
    default: null,
  },
  funnelMetadata: {
    type: Object,
    default: () => ({
      updated_at: null,
      name: '',
    }),
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'switch-view',
  'back',
  'funnel-created',
  'save-funnel',
  'discard-changes',
  'create-new-funnel',
  'create-from-template',
  'open-global-ai',
]);
const { t } = useI18n();
const showNewFunnelModal = ref(false);
const showTemplatesModal = ref(false);
const selectedTemplate = ref(null);
const copiedId = ref(false);

const { isStacklab } = useConfig();

// Adicione um template vazio padrão
const emptyTemplate = {
  name: '',
  description: '',
  stages: {},
  settings: {
    agents: [],
  },
};

// Templates de funis pré-definidos
const funnelTemplates = [
  {
    id: 'sales',
    name: 'Funil de Vendas',
    icon: 'briefcase',
    description: 'Processo de vendas desde o primeiro contato até o fechamento',
    stages: {
      prospeccao: {
        name: 'Prospecção',
        color: '#6366f1',
        position: 1,
        description: 'Leads e primeiros contatos',
        message_templates: [
          {
            id: `template_${Date.now()}_1`,
            title: 'Boas vindas',
            content: 'Olá! Obrigado por entrar em contato. Como posso ajudar?',
            webhook: {
              url: '',
              method: 'POST',
              enabled: false,
            },
            stage_id: 'prospeccao',
            conditions: {
              rules: [],
              enabled: false,
            },
            created_at: new Date().toISOString(),
          },
        ],
      },
      qualificacao: {
        name: 'Qualificação',
        color: '#8b5cf6',
        position: 2,
        description: 'Análise de fit',
        message_templates: [
          {
            id: `template_${Date.now()}_2`,
            title: 'Agendamento de Reunião',
            content:
              'Podemos agendar uma reunião para entender melhor suas necessidades?',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'qualificacao',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      proposta: {
        name: 'Proposta',
        color: '#ec4899',
        position: 3,
        description: 'Envio e negociação',
      },
      fechamento: {
        name: 'Fechamento',
        color: '#10b981',
        position: 4,
        description: 'Contratos e pagamentos',
      },
    },
  },
  {
    id: 'support',
    name: 'Funil de Suporte',
    icon: 'chat',
    description: 'Atendimento e resolução de tickets de suporte',
    stages: {
      novo: {
        name: 'Novo',
        color: '#6366f1',
        position: 1,
        description: 'Tickets recém abertos',
        message_templates: [
          {
            id: `template_${Date.now()}_3`,
            title: 'Confirmação de Recebimento',
            content: 'Recebemos seu ticket. Nossa equipe já está analisando.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'novo',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      analise: {
        name: 'Em Análise',
        color: '#8b5cf6',
        position: 2,
        description: 'Avaliação técnica',
        message_templates: [
          {
            id: `template_${Date.now()}_4`,
            title: 'Atualização de Status',
            content: 'Seu ticket está em análise pela nossa equipe técnica.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'analise',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      desenvolvimento: {
        name: 'Em Desenvolvimento',
        color: '#ec4899',
        position: 3,
        description: 'Correção em andamento',
      },
      teste: {
        name: 'Em Teste',
        color: '#f59e0b',
        position: 4,
        description: 'Validação da solução',
      },
      resolvido: {
        name: 'Resolvido',
        color: '#10b981',
        position: 5,
        description: 'Ticket finalizado',
      },
    },
  },
  {
    id: 'recruitment',
    name: 'Funil de Recrutamento',
    icon: 'people',
    description: 'Processo seletivo e contratação',
    stages: {
      triagem: {
        name: 'Triagem',
        color: '#6366f1',
        position: 1,
        description: 'Análise de currículos',
      },
      entrevista: {
        name: 'Entrevista',
        color: '#8b5cf6',
        position: 2,
        description: 'Conversa inicial',
      },
      teste: {
        name: 'Teste Técnico',
        color: '#ec4899',
        position: 3,
        description: 'Avaliação prática',
      },
      proposta: {
        name: 'Proposta',
        color: '#10b981',
        position: 4,
        description: 'Negociação final',
      },
    },
  },
  {
    id: 'delivery',
    name: 'Funil de Delivery',
    icon: 'truck',
    description: 'Processo de entrega desde o pedido até a finalização',
    stages: {
      pedido_recebido: {
        name: 'Pedido Recebido',
        color: '#6366f1',
        position: 1,
        description: 'Pedido confirmado e recebido',
        message_templates: [
          {
            id: `template_${Date.now()}_5`,
            title: 'Confirmação de Pedido',
            content:
              'Seu pedido foi recebido com sucesso! Estamos preparando sua entrega.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'pedido_recebido',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      preparacao: {
        name: 'Preparação',
        color: '#8b5cf6',
        position: 2,
        description: 'Produto sendo preparado',
        message_templates: [
          {
            id: `template_${Date.now()}_6`,
            title: 'Pedido em Preparação',
            content: 'Seu pedido está sendo preparado e logo será enviado.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'preparacao',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      em_transito: {
        name: 'Em Trânsito',
        color: '#ec4899',
        position: 3,
        description: 'A caminho do centro de distribuição',
      },
      saiu_entrega: {
        name: 'Saiu para Entrega',
        color: '#f59e0b',
        position: 4,
        description: 'A caminho do endereço de entrega',
        message_templates: [
          {
            id: `template_${Date.now()}_7`,
            title: 'Saiu para Entrega',
            content: 'Seu pedido saiu para entrega! Você receberá em breve.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'saiu_entrega',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      entregue: {
        name: 'Entregue',
        color: '#10b981',
        position: 5,
        description: 'Pedido entregue com sucesso',
      },
    },
  },
];

const handleTemplateSelect = template => {
  // Formata o template igual ao formato de edição
  const formattedTemplate = {
    name: template.name,
    description: template.description,
    stages: Object.entries(template.stages).reduce(
      (acc, [id, stage]) => ({
        ...acc,
        [id]: {
          ...stage,
          id,
          message_templates: stage.message_templates || [], // Preserva os templates existentes
          position: stage.position, // Garante que a posição é mantida
        },
      }),
      {}
    ),
    settings: {
      agents: [],
    },
  };

  // Ordena as etapas por posição
  const orderedStages = {};
  Object.entries(formattedTemplate.stages)
    .sort(([, a], [, b]) => a.position - b.position)
    .forEach(([id, stage]) => {
      orderedStages[id] = stage;
    });

  formattedTemplate.stages = orderedStages;

  // Envia para o pai abrir a view de criação com o template
  showTemplatesModal.value = false;
  emit('create-from-template', formattedTemplate);
};

const handleBack = () => {
  // Se estiver editando ou criando, emitir 'back' para voltar à lista de funis
  // Caso contrário, emitir 'switch-view' para voltar ao kanban
  if (props.editingFunnel || props.isCreating) {
    emit('back');
  } else {
    emit('switch-view', 'kanban');
  }
};

const handleFunnelCreated = funnel => {
  showNewFunnelModal.value = false;
  emit('funnel-created', funnel);
};

const handleSaveFunnel = () => {
  emit('save-funnel');
};

const handleDiscardChanges = () => {
  emit('discard-changes');
};

const copyFunnelId = async () => {
  if (props.editingFunnel?.id) {
    await navigator.clipboard.writeText(props.editingFunnel.id.toString());
    copiedId.value = true;
    setTimeout(() => {
      copiedId.value = false;
    }, 2000);
  }
};
</script>

<template>
  <header class="funnels-header">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-chevron-left-icon lucide-chevron-left cursor-pointer text-n-slate-11"
          @click="handleBack"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
        <div class="flex items-center gap-4">
          <h1 class="text-base font-medium flex items-center gap-2">
            <span class="hidden md:inline">{{
              editingFunnel
                ? 'Editando:'
                : isCreating
                  ? 'Criando:'
                  : t('KANBAN.FUNNELS.TITLE')
            }}</span>
            <span v-if="editingFunnel">{{ editingFunnel.name }}</span>
            <span v-if="isCreating">Novo Funil</span>
            <span
              v-if="editingFunnel"
              class="hidden md:flex px-2 py-1 text-xs bg-n-solid-2 text-n-slate-11 rounded flex items-center gap-2 cursor-pointer hover:bg-n-alpha-2 transition-colors"
              title="Clique para copiar"
              @click="copyFunnelId"
            >
              <span class="text-[10px] text-n-slate-11 mr-1">ID:</span>{{ editingFunnel.id }}
              <svg
                v-if="!copiedId"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-copy-icon lucide-copy"
              >
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path
                  d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-circle-check-big-icon lucide-circle-check-big"
              >
                <path d="M21.801 10A10 10 0 1 1 17 3.335" />
                <path d="m9 11 3 3L22 4" />
              </svg>
              <span class="text-[10px] text-n-slate-11">{{
                copiedId ? 'Copiado' : 'Copiar'
              }}</span>
            </span>
          </h1>
          <div
            v-if="editingFunnel && funnelMetadata.updated_at"
            class="hidden md:block text-xs text-n-slate-11"
          >
            Atualizado em:
            {{ new Date(funnelMetadata.updated_at).toLocaleString() }}
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <!-- Botões quando NÃO está editando nem criando -->
        <template v-if="!editingFunnel && !isCreating">
          <Button
            v-if="isStacklab === true"
            variant="ghost"
            color="slate"
            size="sm"
            @click="showTemplatesModal = true"
          >
            <template #icon>
              <span class="i-ri-file-copy-line size-5" />
            </template>
            <span>Modelos</span>
          </Button>
          <!-- SiteUp: regra GLOBAL de IA (multi-funil) — mora na conta, por isso
               fica aqui na lista e não dentro da edição de um funil. -->
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="emit('open-global-ai')"
          >
            <template #icon>
              <span class="i-ri-sparkling-line size-4" />
            </template>
            <span>IA do Kanban</span>
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            @click="emit('create-new-funnel')"
          >
            <template #icon>
              <span class="i-ri-add-line size-5" />
            </template>
            <span>{{ t('KANBAN.FUNNELS.ACTIONS.NEW') }}</span>
          </Button>
        </template>

        <!-- Botões quando ESTÁ editando ou criando -->
        <template v-else>
          <div class="flex items-center gap-3">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="handleDiscardChanges"
            >
              <span>Descartar</span>
            </Button>
          </div>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            @click="handleSaveFunnel"
          >
            <span>Salvar</span>
          </Button>
        </template>
      </div>
    </div>

    <!-- Modal de Templates -->
    <Modal
      v-model:show="showTemplatesModal"
      :on-close="() => (showTemplatesModal = false)"
      size="medium"
    >
      <div class="p-6">
        <h3 class="text-lg font-extrabold mb-2">
          {{ t('KANBAN.FUNNELS.TEMPLATES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('KANBAN.FUNNELS.TEMPLATES.SELECT_HINT') }}
        </p>
        <div class="grid grid-cols-2 gap-4">
          <div
            v-for="template in funnelTemplates"
            :key="template.id"
            class="p-4 border border-n-weak rounded-xl hover:border-woot-500 dark:hover:border-woot-500 cursor-pointer transition-colors"
            @click="handleTemplateSelect(template)"
          >
            <div class="flex items-center gap-2 mb-2">
              <svg
                v-if="template.id === 'delivery'"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-truck-icon lucide-truck text-woot-500"
              >
                <path
                  d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"
                />
                <path d="M15 18H9" />
                <path
                  d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"
                />
                <circle cx="17" cy="18" r="2" />
                <circle cx="7" cy="18" r="2" />
              </svg>
              <FluentIcon
                v-else
                :icon="template.icon"
                size="20"
                :icons="dashboardIcons"
                class="text-woot-500"
              />
              <h4 class="font-medium">{{ template.name }}</h4>
            </div>
            <p class="text-sm text-n-slate-11 mb-3">
              {{ template.description }}
            </p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="stage in Object.values(template.stages)"
                :key="stage.name"
                class="px-2 py-1 text-xs rounded-full"
                :style="{
                  backgroundColor: `${stage.color}20`,
                  color: stage.color,
                }"
              >
                {{ stage.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </header>
</template>

<style lang="scss" scoped>
.funnels-header {
  padding: var(--space-normal);
  border-bottom: 1px solid var(--color-border);
  background: var(--white);
  margin-bottom: 16px;

  @apply bg-n-solid-1;

  h1 {
    @apply text-n-slate-12;
  }
}

:deep(.funnel-modal) {
  .modal-container {
    @apply max-w-[1024px] w-[90vw] bg-n-solid-1;

    h3 {
      @apply text-n-slate-12;
    }
  }
}
</style>
